import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import GraphComponent from '../copilotComponents/GraphComponent';
import TableComponent from '../copilotComponents/TableComponent';
import ImageComponent from '../copilotComponents/ImageComponent';

const FULL_ID_ANCHOR_RE = /^[ \t>*-]*\[(agent_[^_\]\s]+_(table|image|graph)_\d+_\d+)\][ \t]*$/;
const LEGACY_ANCHOR_RE = /^[ \t>*-]*\[(table|image|graph)_(\d+)\][ \t]*$/;
const ANGLE_ANCHOR_RE = /^[ \t>*-]*<\s*(HTMLTABLE|PLOTLYGRAPH|IMAGE)(\d+)\s*>[ \t]*$/i;
const RESOLVED_LABEL_ANCHOR_RE = /^[ \t>*-]*(Table|Figure)\s+(\d+)\.?\s*$/i;
// Chip body is [^<]*, not .*?: with .*? the closing </span> can backtrack to a LATER span on
// the same line, so "chip, prose, citation chip" matched as an anchor and the whole line —
// prose included — was swallowed by the artifact.
const CHIP_SPAN_ANCHOR_RE = /^[ \t>*-]*<span[^>]*data-ref="\[?(agent_[^_\]\s]+_(?:table|image|graph)_\d+_\d+)\]?"[^>]*>[^<]*<\/span>[ \t]*$/i;
const COLOR_TAG_OPEN_RE = /<(div|span)\s+style="color:([^"]*)"[^>]*>/gi;
const COLOR_TAG_CLOSE_RE = /<\/(div|span)>/gi;

const getColorStateAfter = (text, enteringState) => {
  const events = [];
  COLOR_TAG_OPEN_RE.lastIndex = 0;
  COLOR_TAG_CLOSE_RE.lastIndex = 0;

  let match;
  while ((match = COLOR_TAG_OPEN_RE.exec(text)) !== null) {
    events.push({
      index: match.index,
      type: 'open',
      tag: match[1].toLowerCase(),
      color: match[2],
    });
  }
  while ((match = COLOR_TAG_CLOSE_RE.exec(text)) !== null) {
    events.push({
      index: match.index,
      type: 'close',
      tag: match[1].toLowerCase(),
    });
  }

  events.sort((a, b) => a.index - b.index);

  let state = enteringState;
  events.forEach(event => {
    if (event.type === 'open') {
      state = { tag: event.tag, color: event.color };
    } else if (state && state.tag === event.tag) {
      state = null;
    }
  });

  return state;
};

const splitMarkdownAtArtifactAnchors = (
  markdown,
  artifactById,
  artifactByLegacyKey,
  artifactByAngleKey,
  artifactByResolvedLabel,
  processMarkdown
) => {
  if (!markdown) return { segments: [], consumed: new Set() };

  const segments = [];
  const buffer = [];
  const consumed = new Set();
  let openColorState = null;

  const matchResolvedLabelArtifact = (line) => {
    const labelMatch = line.match(RESOLVED_LABEL_ANCHOR_RE);
    if (!labelMatch) return null;
    return artifactByResolvedLabel[`${labelMatch[1].toLowerCase()}_${labelMatch[2]}`] || null;
  };

  // Only a line that holds nothing but the reference places its artifact. A reference cited
  // inside a sentence stays a citation: the artifact belongs at its own placeholder line, or,
  // if it has none, in the fallback block below the text. Placing it at the mention would cut
  // the sentence in two, because markdown cannot put a table inside a paragraph.
  const flushText = () => {
    if (buffer.length === 0) return;
    let text = buffer.join('\n').replace(/^\n+|\n+$/g, '');
    buffer.length = 0;
    if (!text.trim()) return;

    const enteringState = openColorState;
    const exitingState = getColorStateAfter(text, enteringState);

    if (enteringState) {
      text = `<${enteringState.tag} style="color:${enteringState.color}">\n${text}`;
    }
    if (exitingState) {
      text = `${text}\n</${exitingState.tag}>`;
    }

    segments.push({ type: 'text', text });
    openColorState = exitingState;
  };

  const consumeArtifact = (artifact) => {
    if (!artifact) return false;
    if (consumed.has(artifact.id)) return false;
    flushText();
    segments.push(artifact);
    consumed.add(artifact.id);
    return true;
  };

  let inQuotationFence = false;
  markdown.split('\n').forEach(line => {
    if (/^:::quotation\s*$/.test(line)) {
      inQuotationFence = true;
      buffer.push(line);
      return;
    }
    if (inQuotationFence && /^:::\s*$/.test(line)) {
      inQuotationFence = false;
      buffer.push(line);
      return;
    }
    if (!line.trim()) {
      buffer.push(line);
      return;
    }

    const fullMatch = line.match(FULL_ID_ANCHOR_RE);
    if (fullMatch) {
      const artifact = artifactById[fullMatch[1]];
      if (consumeArtifact(artifact)) return;
    }

    const chipMatch = line.match(CHIP_SPAN_ANCHOR_RE);
    if (chipMatch) {
      const artifact = artifactById[chipMatch[1]];
      if (consumeArtifact(artifact)) return;
    }

    const legacyMatch = line.match(LEGACY_ANCHOR_RE);
    if (legacyMatch) {
      const key = `${legacyMatch[1]}_${legacyMatch[2]}`;
      const artifact = artifactByLegacyKey[key];
      if (consumeArtifact(artifact)) return;
    }

    const angleMatch = line.match(ANGLE_ANCHOR_RE);
    if (angleMatch) {
      const key = `${angleMatch[1].toLowerCase()}_${angleMatch[2]}`;
      const artifact = artifactByAngleKey[key];
      if (consumeArtifact(artifact)) return;
    }

    if (consumeArtifact(matchResolvedLabelArtifact(line))) return;

    const processedLine = processMarkdown ? processMarkdown(line) : line;
    if (processedLine !== line && consumeArtifact(matchResolvedLabelArtifact(processedLine))) return;

    buffer.push(line);
  });

  flushText();

  return { segments, consumed };
};

const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeRaw];

const MarkdownBlock = React.memo(({ markdown, markdownComponents, processMarkdown }) => {
  const displayMarkdown = useMemo(
    () => (processMarkdown ? processMarkdown(markdown) : markdown),
    [markdown, processMarkdown]
  );
  if (!displayMarkdown || !displayMarkdown.trim()) return null;

  return (
    <div className="markdown-justified mb-3 ev-small-p-dark">
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={markdownComponents}
      >
        {displayMarkdown}
      </ReactMarkdown>
    </div>
  );
});

const InlineArtifactRenderer = ({
  markdown,
  tables = [],
  graphs = [],
  images = [],
  referencedArtifacts = {},
  markdownComponents,
  processMarkdown,
  inPlaceGraphMap = {},
  inPlaceImageMap = {},
  onTableClick,
  onInPlaceGraphView,
  docs = [],
  refs = [],
  showMergeStatus = false,
}) => {
  const { segments, consumed } = useMemo(() => {
    const artifactById = {};
    const artifactByLegacyKey = {};
    const artifactByAngleKey = {};
    const artifactByResolvedLabel = {};

    const mapTableArtifact = (table, index, includeLegacyKeys = true) => {
      const id = table.table_id || `table_${index + 1}`;
      const artifact = { type: 'table', id, data: table, index };
      if (table.table_id && !artifactById[table.table_id]) artifactById[table.table_id] = artifact;
      if (includeLegacyKeys) {
        artifactByLegacyKey[`table_${index + 1}`] = artifact;
        artifactByAngleKey[`htmltable_${index + 1}`] = artifact;
      }
      const tableNumber = table.tableNumber ?? table.table_number ?? (index + 1);
      if (!artifactByResolvedLabel[`table_${tableNumber}`]) {
        artifactByResolvedLabel[`table_${tableNumber}`] = artifact;
      }
    };

    const mapGraphArtifact = (graph, index, includeLegacyKeys = true) => {
      const id = graph.graph_id || `graph_${index + 1}`;
      const artifact = { type: 'graph', id, data: graph, index };
      if (graph.graph_id && !artifactById[graph.graph_id]) artifactById[graph.graph_id] = artifact;
      if (includeLegacyKeys) {
        artifactByLegacyKey[`graph_${index + 1}`] = artifact;
        artifactByAngleKey[`plotlygraph_${index + 1}`] = artifact;
      }
      const figureNumber = graph.figureNumber ?? graph.graph_number ?? (index + 1);
      if (!artifactByResolvedLabel[`figure_${figureNumber}`]) {
        artifactByResolvedLabel[`figure_${figureNumber}`] = artifact;
      }
    };

    const mapImageArtifact = (image, index, includeLegacyKeys = true) => {
      const id = image.image_id || `image_${index + 1}`;
      const artifact = { type: 'image', id, data: image, index };
      if (image.image_id && !artifactById[image.image_id]) artifactById[image.image_id] = artifact;
      if (includeLegacyKeys) {
        artifactByLegacyKey[`image_${index + 1}`] = artifact;
        artifactByAngleKey[`image_${index + 1}`] = artifact;
      }
      const figureNumber = image.figureNumber ?? image.img_num ?? (index + 1);
      if (!artifactByResolvedLabel[`figure_${figureNumber}`]) {
        artifactByResolvedLabel[`figure_${figureNumber}`] = artifact;
      }
    };

    tables.forEach((table, index) => mapTableArtifact(table, index));
    graphs.forEach((graph, index) => mapGraphArtifact(graph, index));
    images.forEach((image, index) => mapImageArtifact(image, index));

    (referencedArtifacts.tables || []).forEach((table, index) => mapTableArtifact(table, index, false));
    (referencedArtifacts.graphs || []).forEach((graph, index) => mapGraphArtifact(graph, index, false));
    (referencedArtifacts.images || []).forEach((image, index) => mapImageArtifact(image, index, false));

    return splitMarkdownAtArtifactAnchors(
      markdown,
      artifactById,
      artifactByLegacyKey,
      artifactByAngleKey,
      artifactByResolvedLabel,
      processMarkdown
    );
  }, [markdown, tables, graphs, images, referencedArtifacts, processMarkdown]);

  const fallbackSegments = useMemo(() => {
    const items = [];
    graphs.forEach((graph, index) => {
      const id = graph.graph_id || `graph_${index + 1}`;
      if (!consumed.has(id)) items.push({ type: 'graph', id, data: graph, index });
    });
    images.forEach((image, index) => {
      const id = image.image_id || `image_${index + 1}`;
      if (!consumed.has(id)) items.push({ type: 'image', id, data: image, index });
    });
    tables.forEach((table, index) => {
      const id = table.table_id || `table_${index + 1}`;
      if (!consumed.has(id)) items.push({ type: 'table', id, data: table, index });
    });
    return items;
  }, [tables, graphs, images, consumed]);

  const renderSegment = (segment, keyPrefix = 'segment', isFallback = false) => {
    if (segment.type === 'text') {
      return (
        <MarkdownBlock
          key={`${keyPrefix}-text`}
          markdown={segment.text}
          markdownComponents={markdownComponents}
          processMarkdown={processMarkdown}
        />
      );
    }

    if (segment.type === 'table') {
      const table = segment.data;
      return (
        <div
          key={`${keyPrefix}-table-${table.table_id || segment.index}`}
          className="genbi-inline-artifact"
          onClick={onTableClick}
        >
          <TableComponent
            tableData={table}
            isLoading={false}
            contentId={table.tableNumber ?? table.table_number ?? (segment.index + 1)}
            subtitle={table.subtitle}
            footer={table.footnote ?? table.footer ?? null}
            inPlaceGraphMap={inPlaceGraphMap}
            inPlaceImageMap={inPlaceImageMap}
            onInPlaceGraphView={onInPlaceGraphView}
            hideContentLabel={!isFallback}
            docs={docs}
            refs={refs}
            showMergeStatus={showMergeStatus}
          />
        </div>
      );
    }

    if (segment.type === 'graph') {
      const graph = segment.data;
      return (
        <div
          key={`${keyPrefix}-graph-${graph.graph_id || segment.index}`}
          className="genbi-inline-artifact"
        >
          <GraphComponent
            graphData={graph}
            isLoading={false}
            contentId={graph.figureNumber ?? graph.graph_number ?? (segment.index + 1)}
            subtitle={graph.subtitle}
            footer={graph.footer ?? null}
            hideContentLabel={!isFallback}
            docs={docs}
            refs={refs}
          />
        </div>
      );
    }

    if (segment.type === 'image') {
      const image = segment.data;
      return (
        <div
          key={`${keyPrefix}-image-${image.image_id || segment.index}`}
          className="genbi-inline-artifact"
        >
          <ImageComponent
            imageData={image}
            isLoading={false}
            contentId={image.figureNumber ?? image.img_num ?? (segment.index + 1)}
            subtitle={image.subtitle}
            footer={image.footer ?? null}
            hideContentLabel={!isFallback}
            docs={docs}
            refs={refs}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {segments.map((segment, index) => renderSegment(segment, `inline-${index}`))}
      {fallbackSegments.length > 0 && (
        <div className="genbi-inline-artifact-fallback">
          {fallbackSegments.map((segment, index) => renderSegment(segment, `fallback-${index}`, true))}
        </div>
      )}
    </>
  );
};

export default InlineArtifactRenderer;
